import { useState, useEffect, useRef } from 'react'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import Card from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import { storiesApi, type StoryApiDto } from '@/services/storiesApiService'
import { getLocalizedValue } from '@sakeena/shared'

declare global {
  interface Window {
    YT?: { Player: new (el: string | HTMLElement, opts: { videoId: string; events?: { onReady?: (e: { target: YTPlayer }) => void } }) => YTPlayer; PlayerState: { PLAYING: number; PAUSED: number; ENDED: number } }
    onYouTubeIframeAPIReady?: () => void
  }
}
interface YTPlayer {
  playVideo: () => void
  pauseVideo: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  getCurrentTime: () => number
  getDuration: () => number
  mute: () => void
  unMute: () => void
  getPlayerState: () => number
  destroy: () => void
  loadVideoById: (videoId: string) => void
}

function resolveVideoUrl(v: string | Record<string, string> | null | undefined, lang: string): string | null {
  if (v == null) return null
  if (typeof v === 'string') return v.trim() || null
  const s = getLocalizedValue(v, lang)
  return s?.trim() || null
}
import { useTranslation } from 'react-i18next'
import { useCurrentLanguage, isStoryVisible } from '@/utils/contentVisibility'
import { useLocale } from '@/hooks/useLocale'

export interface Story {
  id: string
  title: any
  author: string
  region: string
  thumbnail: string
  videoUrl?: string | null
}

export default function StoriesCarousel({ items }: { items?: Story[] }) {
  const { t } = useTranslation()
  const { lang: urlLang } = useLocale()
  const currentLang = useCurrentLanguage()
  const [slides, setSlides] = useState<Story[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const ytPlayerRef = useRef<YTPlayer | null>(null)
  const [youtubeApiReady, setYoutubeApiReady] = useState(false)
  const ytPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Use URL language for filtering so we never show stories without translation for the page language
  const filterLang = urlLang

  // Load stories from backend (US-2.S1). Filter by title available in selected language (from URL).
  useEffect(() => {
    const loadStories = async () => {
      try {
        let candidateStories: Story[] = []

        if (items && items.length > 0) {
          candidateStories = items
        } else {
          const apiStories: StoryApiDto[] = await storiesApi.getStories()
          candidateStories = apiStories.map((s) => ({
            id: s.id,
            title: s.title,
            author: t('home.stories.newMuslim', 'مسلم جديد'),
            region: s.region || t('home.stories.islamicWorld', 'العالم الإسلامي'),
            thumbnail: typeof s.imageUrl === 'string' ? s.imageUrl : (s.imageUrl && getLocalizedValue(s.imageUrl, currentLang)) || '/placeholders/story.jpg',
            videoUrl: resolveVideoUrl(s.videoUrl ?? s.imageUrl, currentLang),
          }))
        }

        // Only show stories that have a title translation for the current page language (URL)
        const visibleStories = candidateStories.filter((s) => isStoryVisible(s, filterLang))
        setSlides(visibleStories)
      } catch (err) {
        console.error('Failed to load stories:', err)
        setSlides([])
      } finally {
        setLoading(false)
      }
    }

    loadStories()
  }, [items, t, currentLang, filterLang])

  useEffect(() => {
    setCurrentIndex((prev) => (slides.length ? Math.min(prev, slides.length - 1) : 0))
  }, [slides.length])

  // تحميل YouTube IFrame API مرة واحدة
  useEffect(() => {
    if (typeof window === 'undefined' || window.YT) {
      setYoutubeApiReady(!!window.YT)
      return
    }
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(tag, firstScript)
    window.onYouTubeIframeAPIReady = () => {
      setYoutubeApiReady(true)
      window.onYouTubeIframeAPIReady = undefined
    }
    return () => {
      window.onYouTubeIframeAPIReady = undefined
    }
  }, [])

  useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
    if (videoRef.current) {
      videoRef.current.currentTime = 0
    }
  }, [currentIndex])

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const togglePlayPause = () => {
    if (useYoutubeEmbed && ytPlayerRef.current) {
      const p = ytPlayerRef.current
      const state = p.getPlayerState?.()
      if (state === 1) {
        p.pauseVideo()
        setIsPlaying(false)
      } else {
        p.playVideo()
        setIsPlaying(true)
      }
      return
    }
    const video = videoRef.current
    if (video) {
      if (isPlaying) {
        video.pause()
      } else {
        video.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (useYoutubeEmbed && ytPlayerRef.current) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const pct = x / rect.width
      const d = ytPlayerRef.current.getDuration()
      if (Number.isFinite(d)) {
        ytPlayerRef.current.seekTo(pct * d, true)
        setCurrentTime(pct * d)
      }
      return
    }
    const video = videoRef.current
    if (!video || duration <= 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const pct = x / rect.width
    video.currentTime = pct * duration
  }

  const hasNativeVideo = (story: Story) => {
    if (!story.videoUrl || typeof story.videoUrl !== 'string') return false
    const u = story.videoUrl.trim()
    return (
      u.startsWith('data:video') ||
      (u.startsWith('http') && !u.includes('youtube.com') && !u.includes('youtu.be'))
    )
  }

  // كل القيم المستخدمة في الـ hooks يجب أن تُحسب قبل أي return (قواعد الـ hooks)
  const currentStory = slides.length > 0 ? slides[Math.min(currentIndex, slides.length - 1)] : null
  const getVideoEmbedUrl = (url: string): string => {
    if (!url || typeof url !== 'string' || url.trim().length === 0) return ''
    if (url.includes('youtube.com') && (url.endsWith('v=') || url.endsWith('v=f:') || url.match(/v=[^&]{0,2}$/))) return ''
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const youtubeId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]
      if (youtubeId && youtubeId.length === 11) return `https://www.youtube.com/embed/${youtubeId}`
      return ''
    }
    return url
  }
  const getYoutubeThumbnailUrl = (url: string | null | undefined): string | null => {
    if (!url || typeof url !== 'string') return null
    const id = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]
    if (id && id.length === 11) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
    return null
  }
  const getYoutubeVideoId = (url: string | null | undefined): string | null => {
    if (!url || typeof url !== 'string') return null
    const id = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]
    return id && id.length === 11 ? id : null
  }
  const hasVideo = currentStory && currentStory.videoUrl &&
    currentStory.videoUrl.trim().length > 0 &&
    (currentStory.videoUrl.includes('youtube.com') ||
     currentStory.videoUrl.includes('youtu.be') ||
     (currentStory.videoUrl.startsWith('http') && !currentStory.videoUrl.startsWith('data:image')) ||
     currentStory.videoUrl.startsWith('data:video'))
  const isBase64Image = currentStory?.videoUrl?.startsWith('data:image')
  const videoEmbedUrl = currentStory && hasVideo && !isBase64Image && currentStory.videoUrl
    ? getVideoEmbedUrl(currentStory.videoUrl)
    : null
  const showNativeVideo = currentStory && hasVideo && !isBase64Image && !videoEmbedUrl && hasNativeVideo(currentStory)
  const youtubeThumbnail = getYoutubeThumbnailUrl(currentStory?.videoUrl ?? null)
  const displayThumbnail = currentStory && ((currentStory.thumbnail && currentStory.thumbnail.trim() !== '')
    ? currentStory.thumbnail
    : (videoEmbedUrl && youtubeThumbnail) ? youtubeThumbnail : null)
  const youtubeVideoId = getYoutubeVideoId(currentStory?.videoUrl ?? null)
  const useYoutubeEmbed = !!(videoEmbedUrl && youtubeApiReady && youtubeVideoId)

  // التهام خطأ removeChild من يوتيوب أينما حدث (سواء من destroy أو من الـ iframe)
  useEffect(() => {
    const onError = (event: ErrorEvent): boolean => {
      const msg = event.message || ''
      if (/removeChild|NotFoundError|not a child/i.test(msg)) {
        event.preventDefault()
        event.stopPropagation()
        return true
      }
      return false
    }
    const onRejection = (event: PromiseRejectionEvent): void => {
      const msg = event.reason?.message ?? String(event.reason ?? '')
      if (/removeChild|NotFoundError|not a child/i.test(msg)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  // إنشاء مشغّل يوتيوب عند الحاجة — لا نستدعي destroy() أبداً لتجنّب خطأ removeChild
  useEffect(() => {
    if (!useYoutubeEmbed) {
      ytPlayerRef.current = null
      return
    }
    const el = document.getElementById('stories-yt-player')
    if (!el || !window.YT || !youtubeVideoId) return
    if (ytPlayerRef.current) return
    const YT = window.YT
    new YT.Player('stories-yt-player', {
      videoId: youtubeVideoId,
      events: {
        onReady(e: { target: YTPlayer }) {
          const p = e.target
          ytPlayerRef.current = p
          const d = p.getDuration()
          if (Number.isFinite(d)) setDuration(d)
          if (muted) p.mute()
        },
      },
    })
    return () => {
      ytPlayerRef.current = null
    }
  }, [useYoutubeEmbed, youtubeApiReady])

  // عند تغيير القصة (السابق/التالي) ونفس الوضع يوتيوب: تحميل الفيديو الجديد بدون destroy
  useEffect(() => {
    if (!useYoutubeEmbed || !youtubeVideoId || !ytPlayerRef.current) return
    ytPlayerRef.current.loadVideoById(youtubeVideoId)
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }, [youtubeVideoId, useYoutubeEmbed])

  // تحديث الوقت الحالي عند تشغيل فيديو يوتيوب — قبل أي return
  useEffect(() => {
    if (!useYoutubeEmbed || !isPlaying) return
    ytPollRef.current = setInterval(() => {
      const p = ytPlayerRef.current
      if (p) {
        const t = p.getCurrentTime()
        if (Number.isFinite(t)) setCurrentTime(t)
        const state = p.getPlayerState?.()
        if (state === 0) setIsPlaying(false) // ENDED
      }
    }, 300)
    return () => {
      if (ytPollRef.current) {
        clearInterval(ytPollRef.current)
        ytPollRef.current = null
      }
    }
  }, [useYoutubeEmbed, isPlaying])

  if (loading) {
    return (
      <section className="pt-0 pb-16 bg-white dark:bg-gray-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('home.stories.title', 'قصص ملهمة لمسلمين جدد')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{t('home.stories.subtitle', 'قصص حقيقية من أشخاص دخلوا الإسلام')}</p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </section>
    )
  }

  if (slides.length === 0) {
    return null
  }

  if (!currentStory) {
    return null
  }

  return (
    <section className="pt-0 pb-8 sm:pb-12 md:pb-16 bg-white dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* عنوان القسم خارج الكارت - على اليمين وبنفس style الـ Figma */}
        <div className="text-right mb-6 md:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('home.stories.title', 'قصص ملهمة لمسلمين جدد')}
          </h2>
          <p className="text-base sm:text-lg text-gray-400 dark:text-gray-500 mt-1">
            {t('home.stories.title', 'قصص ملهمة لمسلمين جدد')}
          </p>
        </div>

        <Card className="flex flex-col overflow-hidden rounded-2xl md:rounded-3xl border-0 shadow-[0_4px_20px_rgba(0,0,0,0.08),0_0_1px_rgba(0,0,0,0.06)] p-0 gap-0">
          {/* صف المحتوى: بلاير + قائمة (بدون عنوان داخل الكارت) */}
          <div className="flex flex-col md:flex-row items-stretch min-h-0">
            {/* لوحة المشغّل - بدون border radius للفيديو، مع padding مثل Figma */}
            <div className="w-full md:w-[65%] flex flex-col order-2 md:order-1 rtl:md:order-2 flex-shrink-0 bg-white dark:bg-gray-800 p-4 sm:p-5 md:p-6">
              <div className="relative overflow-hidden bg-black rounded-none" style={{ aspectRatio: '16/9', maxHeight: '320px' }}>
              {useYoutubeEmbed ? (
                <div id="stories-yt-player" className="absolute inset-0 w-full h-full" />
              ) : videoEmbedUrl ? (
                /* رابط يوتيوب: ثمبنيل من القصة أو من يوتيوب + زر تشغيل */
                <>
                  {displayThumbnail ? (
                    <img
                      src={displayThumbnail}
                      alt={getLocalizedValue(currentStory.title, currentLang)}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.currentTarget
                        if (youtubeThumbnail && target.src !== youtubeThumbnail) {
                          target.src = youtubeThumbnail
                        } else {
                          target.style.display = 'none'
                        }
                      }}
                    />
                  ) : youtubeThumbnail ? (
                    <img
                      src={youtubeThumbnail}
                      alt={getLocalizedValue(currentStory.title, currentLang)}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gray-800 flex items-center justify-center text-gray-500 text-sm">
                      {getLocalizedValue(currentStory.title, currentLang)}
                    </div>
                  )}
                  <a
                    href={currentStory.videoUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                    aria-label={t('common.play', 'تشغيل')}
                  >
                    <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/70 flex items-center justify-center text-gray-900 shadow-lg hover:bg-white/80">
                      <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-gray-900 ms-0.5" />
                    </span>
                  </a>
                </>
              ) : showNativeVideo ? (
                <video
                  ref={videoRef}
                  src={currentStory.videoUrl || ''}
                  className="absolute inset-0 w-full h-full object-cover"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
                  onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
                  onEnded={() => setIsPlaying(false)}
                  onClick={togglePlayPause}
                >
                  {t('common.videoNotSupported', 'متصفحك لا يدعم تشغيل الفيديو')}
                </video>
              ) : isBase64Image ? (
                <img
                  src={currentStory.videoUrl || ''}
                  alt={getLocalizedValue(currentStory.title, currentLang)}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <img
                  src={currentStory.thumbnail}
                  alt={getLocalizedValue(currentStory.title, currentLang)}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              {/* أيقونة تشغيل على الثمبنيل - نفس الشكل: دائرة بيضاء شفافة + مثلث play */}
              {(!videoEmbedUrl && (showNativeVideo || !hasVideo)) && (
                <button
                  type="button"
                  onClick={showNativeVideo ? togglePlayPause : undefined}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                  aria-label={isPlaying ? t('common.pause', 'إيقاف') : t('common.play', 'تشغيل')}
                >
                  <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/70 flex items-center justify-center text-gray-900 shadow-lg hover:bg-white/80">
                    {showNativeVideo && isPlaying ? (
                      <Pause className="w-8 h-8 sm:w-10 sm:h-10 text-gray-900" />
                    ) : (
                      <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-gray-900 ms-0.5" />
                    )}
                  </span>
                </button>
              )}
              </div>

            {/* Track info + progress + controls - padding من الأعلى مثل Figma */}
            <div className="pt-4 sm:pt-5 flex-shrink-0">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-0.5">
                {t('home.stories.inspiringStory', 'قصة مؤثرة لمسلم جديد')}
              </p>
              <p className="font-bold text-gray-900 dark:text-gray-100 text-base sm:text-lg mb-3">
                {getLocalizedValue(currentStory.title, currentLang)}
              </p>
              {/* Progress bar */}
              <div
                className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full cursor-pointer mb-2 overflow-hidden"
                onClick={(showNativeVideo || useYoutubeEmbed) ? handleSeek : undefined}
              >
                <div
                  className="h-full bg-emerald-600 dark:bg-emerald-500 rounded-full transition-all"
                  style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>{formatTime(currentTime)}</span>
                <span>{duration > 0 ? formatTime(duration) : '0:00'}</span>
              </div>
              {/* Controls: volume + play/pause فقط (إخفاء السابق/التالي) */}
              <div className="flex items-center justify-center gap-3 sm:gap-5">
                <button
                  type="button"
                  onClick={() => {
                    if (useYoutubeEmbed && ytPlayerRef.current) {
                      if (muted) ytPlayerRef.current.unMute()
                      else ytPlayerRef.current.mute()
                    }
                    setMuted((m) => !m)
                  }}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  aria-label={muted ? t('common.unmute', 'إلغاء كتم الصوت') : t('common.mute', 'كتم الصوت')}
                >
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                  type="button"
                  onClick={(showNativeVideo || useYoutubeEmbed) ? togglePlayPause : undefined}
                  className="p-2.5 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:opacity-90"
                  aria-label={isPlaying ? t('common.pause', 'إيقاف') : t('common.play', 'تشغيل')}
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current ms-0.5" />}
                </button>
              </div>
            </div>
          </div>

          {/* لوحة القائمة - padding مثل Figma */}
          <div className="w-full md:w-[35%] order-1 md:order-2 rtl:md:order-1 flex-1 min-h-0 flex flex-col border-t md:border-t-0 md:border-s rtl:md:border-s-0 rtl:md:border-e border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 md:rounded-tr-2xl md:rounded-br-2xl rtl:md:rounded-tr-none rtl:md:rounded-tl-2xl rtl:md:rounded-bl-2xl overflow-hidden p-0">
            <div className="flex-1 min-h-0 overflow-y-auto py-2 px-3 sm:px-4">
              {slides.map((story, index) => {
                const isActive = index === currentIndex
                return (
                  <button
                    key={story.id}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      'w-full text-right px-4 sm:px-5 py-3 sm:py-3.5 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0',
                      isActive && 'bg-emerald-50 dark:bg-emerald-900/20'
                    )}
                  >
                    <p className={cn(
                      'font-semibold text-sm text-gray-900 dark:text-gray-100',
                      isActive && 'text-emerald-700 dark:text-emerald-400'
                    )}>
                      {getLocalizedValue(story.title, currentLang)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {story.author} · {story.region}
                    </p>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        </Card>
      </div>
    </section>
  )
}
